import React, { useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import AuthHero from '../components/AuthHero';
import SocialAuthButtons from '../components/SocialAuthButtons';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Login.css';

// Safe redirect: only allow same-origin paths (start with /, no protocol)
function getSafeRedirect(redirectParam) {
  if (!redirectParam || typeof redirectParam !== 'string') return null;
  try {
    const decoded = decodeURIComponent(redirectParam.trim());
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
    return decoded;
  } catch {
    return null;
  }
}

const Login = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirectTo = getSafeRedirect(searchParams.get('redirect'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const { login, isAuthenticated, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  // Redirect if already logged in (by role or redirect param)
  React.useEffect(() => {
    if (isAuthenticated()) {
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else if (isManager()) {
        navigate('/manager/dashboard', { replace: true });
      } else if (isAdmin()) {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, isManager, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple simultaneous submissions using ref
    if (isSubmittingRef.current || loading) {
      return;
    }
    
    isSubmittingRef.current = true;
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Redirect to return URL if present and safe, otherwise by role
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
        } else {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          if (userData.role === 'admin') {
            navigate('/admin/dashboard', { replace: true });
          } else if (userData.role === 'manager') {
            navigate('/manager/dashboard', { replace: true });
          } else {
            navigate('/admin', { replace: true });
          }
        }
      } else {
        setError(result.error);
        if (result.emailNotVerified && result.email) {
          setShowResendVerification(true);
          setUnverifiedEmail(result.email);
        } else {
          setShowResendVerification(false);
          setUnverifiedEmail('');
        }
      }
    } catch (err) {
      setError(t('auth.login.unexpectedError'));
    } finally {
      setLoading(false);
      // Reset after a short delay to allow for any pending state updates
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, 1000);
    }
  };

  return (
    <div className="login-page fade-in">
      <AuthHero />

      <div className="login-form-container">
        <div className="login-form-wrapper">
          <div
            className="login-tabs"
            role="tablist"
            aria-label={t('auth.login.tabs', 'Authentication')}
          >
            <span role="tab" aria-selected="true" className="login-tab active">
              {t('auth.login.signInTab', 'Sign In')}
            </span>
            <Link to="/register" role="tab" aria-selected="false" className="login-tab">
              {t('auth.login.registerTab', 'Register')}
            </Link>
          </div>

          <div className="login-header">
            <h1>{t('auth.login.welcomeBack', 'Welcome back')}</h1>
            <p>{t('auth.login.welcomeBackSubtitle', 'Sign in to access your Mesoon dashboard')}</p>
          </div>

          {error && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
              {showResendVerification && unverifiedEmail && (
                <p style={{ marginTop: 8, fontSize: '0.9rem' }}>
                  <Link to="/resend-verification" state={{ email: unverifiedEmail }}>
                    {t('auth.verification.resendEmail', 'Resend verification email')}
                  </Link>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">{t('auth.login.emailLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  id="email"
                  type="email"
                  className="form-input has-icon"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.login.emailPlaceholder')}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('auth.login.passwordLabel')}</label>
              <div className="input-wrapper">
                <svg className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input has-icon has-toggle"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="login-options-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkbox-custom"></span>
                <span>{t('auth.login.rememberMe')}</span>
              </label>
              <Link to="/forgot-password" className="forgot-link">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full login-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-sm"></span>
                  {t('auth.login.submitting')}
                </>
              ) : (
                <>
                  {t('auth.login.submitButton')}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="login-divider"><span>{t('auth.login.orContinueWith', 'or continue with')}</span></div>

          <SocialAuthButtons redirectTo={redirectTo} onError={setError} />

          <p className="login-create-account">
            {t('auth.login.noAccount')} <Link to="/register">{t('auth.login.createAccount')}</Link>
          </p>

          <div className="login-meta-row">
            <LanguageSwitcher />
            <span className="login-support">
              {t('auth.login.needHelp', 'Need help?')}{' '}
              <a href="mailto:support@mesoon.app">{t('auth.login.contactSupport', 'Contact support')}</a>
            </span>
          </div>

          <p className="login-copyright">{t('auth.login.copyright', { year: new Date().getFullYear(), defaultValue: '© {{year}} Mesoon. All rights reserved.' })}</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
